import { useEffect } from 'react';
import { useSiteConfig } from '../../helpers/useSiteConfig';
import { initAnalytics } from '../../helpers/consent';

/**
 * Starts (or does not start) GA4 for the active site. The measurement id
 * comes from SiteConfig.ga4 at runtime — one build, three ids. `ga4: null`
 * disables analytics for a site.
 *
 * Was an SSR-injected inline <script> via Helmet, so gtag's bootstrap ran
 * while the initial HTML was still parsing. That is gone (BL-0079): a
 * visitor's region and any stored consent choice decide whether gtag loads
 * at all, and that decision reads document.cookie/Intl/navigator, none of
 * which SSR can see — evaluating it up front in Node would either fabricate
 * a default that ignores the actual browser or duplicate the parser in two
 * places (an inline string AND this component), which is how the two drift
 * apart. So this now runs client-side, after hydration, via
 * helpers/consent.js's initAnalytics — the one place that decision is made.
 * Trade-off, stated plainly: a visitor who leaves before hydration is not
 * counted, where the old inline script would have caught them. Accepted
 * (plan section 3.5).
 *
 * pp_dev semantics are unchanged: ?pp_dev=1 sets a year-long cookie that
 * disables analytics for that browser, ?pp_dev=0 clears it, and the cookie's
 * mere presence means window.gtag stays undefined — see initAnalytics.
 */
const SiteAnalytics = () => {
  const site = useSiteConfig();
  useEffect(() => {
    if (site.ga4) {
      initAnalytics(site.ga4);
    }
  }, [site.ga4]);
  return null;
};

export default SiteAnalytics;
